import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type SessionUser } from '../api/client';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  needsSetup: boolean;
  refresh: () => Promise<void>;
  setUser: (u: SessionUser | null) => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { needsSetup } = await api.setupStatus();
      setNeedsSetup(needsSetup);
      if (!needsSetup) {
        try { setUser(await api.me()); } catch { setUser(null); }
      }
    } catch {
      // API unreachable (e.g. functions not running) — fail safe to logged-out.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  return (
    <Ctx.Provider value={{ user, loading, needsSetup, refresh, setUser }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
